import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, checkImageFile, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useSettings } from "../lib/settings";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { Sparkles, Send, UserPlus } from "lucide-react";
import logoLight from "../assets/showroom360-logo.png";
import { PaymentGatewayPanel } from "../components/PaymentGatewayPanel";
import { PublicTextsFields } from "../components/PublicTextsFields";
import { FormsSettingsPanel } from "../components/FormsSettingsPanel";
import { MyPlanPanel } from "../components/MyPlanPanel";
import { BranchesPanel } from "../components/BranchesPanel";
import { LeadsSettingsPanel, WhatsAppSettingsPanel, NotificationsSettingsPanel } from "../components/CrmSettingsPanels";
import { FaqSettingsPanel } from "../components/FaqSettingsPanel";
import { useLicense } from "../lib/license";

const SUBS = ["identity", "branding", "currency", "tax", "website", "ai", "payments", "forms", "social", "branches", "leads", "whatsapp", "notifications", "plan"];
const PRIMARY_PRESETS = ["#c8102e", "#2447a8", "#1f7a4d", "#141312", "#b45309", "#7c3aed"];
const SECONDARY_PRESETS = ["#f4f3f1", "#ffffff", "#e7e5e1", "#14213d", "#1c1c1e", "#1e3a2e"];

function yiqInk(hex) {
  const s = (hex || "").replace("#", "");
  if (s.length !== 6) return "#111";
  const r = parseInt(s.slice(0, 2), 16), g = parseInt(s.slice(2, 4), 16), b = parseInt(s.slice(4, 6), 16);
  return ((r * 299 + g * 587 + b * 114) / 1000) > 160 ? "#111" : "#fff";
}

function ColorPicker({ label, value, onChange, presets, testid }) {
  return (
    <div style={{ display: "grid", gap: 12 }} data-testid={testid}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {presets.map((c) => (
          <button key={c} type="button" onClick={() => onChange(c)}
            data-testid={`${testid}-swatch-${c.replace('#','')}`}
            aria-label={`${label} ${c}`}
            style={{
              width: 40, height: 40, background: c, border: 0, cursor: "pointer",
              outline: value?.toLowerCase() === c.toLowerCase() ? "2px solid var(--text)" : "1px solid var(--divider)",
              outlineOffset: value?.toLowerCase() === c.toLowerCase() ? "2px" : "0",
            }} />
        ))}
        <input type="color" value={value || presets[0]} onChange={(e) => onChange(e.target.value)}
          style={{ width: 40, height: 40, border: "1px solid var(--divider)", padding: 0, cursor: "pointer" }}
          data-testid={`${testid}-custom`} />
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="input" dir="ltr"
          style={{ maxWidth: 140, fontFamily: "monospace", fontSize: 13 }} data-testid={`${testid}-hex`} />
      </div>
    </div>
  );
}

function ThemePreview({ primary, secondary }) {
  const pInk = yiqInk(primary), sInk = yiqInk(secondary);
  return (
    <div style={{ display: "grid", gap: 2, background: "var(--divider)", border: "1px solid var(--divider)", padding: 2 }} data-testid="theme-preview">
      <div style={{ background: primary, color: pInk, padding: 20, fontWeight: 500, fontSize: 14 }}>
        {tr("x.primary_buttons_active_state_sliders_hig")}
      </div>
      <div style={{ background: secondary, color: sInk, padding: 20, fontWeight: 500, fontSize: 14 }}>
        {tr("x.secondary_sidebar_trade_in_band_finance_")}
      </div>
    </div>
  );
}

const CURRENCIES = [
  { code: "OMR", symbol: "OMR", decimals: 3 },
  { code: "AED", symbol: "AED", decimals: 2 },
  { code: "SAR", symbol: "SAR", decimals: 2 },
  { code: "KWD", symbol: "KWD", decimals: 3 },
  { code: "BHD", symbol: "BHD", decimals: 3 },
  { code: "USD", symbol: "$", decimals: 2 },
  { code: "EUR", symbol: "€", decimals: 2 },
];

export default function SettingsPage() {
  const { loadFull, saveSettings } = useSettings();
  const { user, can } = useAuth();
  const { t } = useT();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  // tab lives in the URL so a remount or reload keeps the user's place
  const [params, setParams] = useSearchParams();
  const active = SUBS.includes(params.get("tab")) ? params.get("tab") : "identity";
  const setActive = (k) => setParams((p) => { p.set("tab", k); return p; }, { replace: true });

  const isAdmin = user?.role === "admin";
  const { has } = useLicense();

  const load = async () => {
    try { setForm(await loadFull()); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [loadFull]);
  if (!form) return <div className="py-10 text-center opacity-60">{tr("x.loading")}</div>;

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      // Read-only / server-computed keys are not part of the update schema.
      const { ai_usage, ai_key_set, ai_api_key_hint, updated_at, primary_accent, secondary_accent, ...body } = form;
      setForm(await saveSettings(body));
      toast.success(tr("x.settings_saved"));
    }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const setCurrency = (code) => {
    const c = CURRENCIES.find((x) => x.code === code);
    if (!c) return;
    setForm({ ...form, currency_code: c.code, currency_symbol: c.symbol, currency_decimals: c.decimals });
  };

  return (
    <div data-testid="settings-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="kicker">{tr("x.white_label_configuration")}</div>
          <h1 className="mt-2" style={{ fontSize: 34, fontWeight: 600, letterSpacing: "-0.02em" }}>{t("settings.title")}</h1>
        </div>
        {can("settings", "edit") ? <div className="flex gap-2">
          <button className="btn-secondary" onClick={load} data-testid="cancel-settings">{tr("x.cancel")}</button>
          <button className="btn-primary" onClick={save} disabled={busy} data-testid="save-settings">
            {busy ? tr("x.saving") : tr("x.save_changes")}
          </button>
        </div> : <div className="text-xs" style={{ color: "var(--text-muted)" }} data-testid="settings-readonly">{tr("x.read_only")}</div>}
      </div>

      <div className="grid gap-6 settings-grid">
        {/* Left sub-nav */}
        <nav className="panel" style={{ alignSelf: "start" }}>
          {SUBS.map((k) => (
            <button
              key={k}
              data-testid={`settings-sub-${k}`}
              onClick={() => setActive(k)}
              aria-current={active === k ? "page" : undefined}
              className="w-full text-start"
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid var(--divider-soft)",
                background: active === k ? "var(--accent-100)" : "transparent",
                borderInlineStart: active === k ? "3px solid var(--accent)" : "3px solid transparent",
                color: active === k ? "var(--accent-700)" : "inherit",
                fontSize: 14, fontWeight: active === k ? 500 : 400,
                display: "block",
              }}
            >
              {t(`settings.sub_${k /* i18n-keys: identity,branding,currency,tax,website,ai,payments,forms,social,plan */}`)}
            </button>
          ))}
        </nav>

        {/* Right panel */}
        <div style={{ minWidth: 0 }}>
          {active === "identity" && (
            <Section title={t("settings.sub_identity")}>
              <Field label={tr("x.showroom_name")}><input value={form.showroom_name || ""} onChange={(e) => patch("showroom_name", e.target.value)} className="input" data-testid="settings-showroom-name" /></Field>
              <Field label={tr("x.legal_name")}><input value={form.legal_name || ""} onChange={(e) => patch("legal_name", e.target.value)} className="input" /></Field>
              <Field label={tr("x.branch")}><input value={form.branch_name || ""} onChange={(e) => patch("branch_name", e.target.value)} placeholder={tr("x.al_ghubra_muscat")} className="input" /></Field>
              <Field label={tr("x.domain")}><input value={form.website || ""} onChange={(e) => patch("website", e.target.value)} className="input" /></Field>
              <Field label={tr("x.address")}><input value={form.address || ""} onChange={(e) => patch("address", e.target.value)} className="input" /></Field>
              <Field label={`${tr("x.address")} · ${tr("x.lang_ar")}`}><input dir="rtl" value={form.address_ar || ""} onChange={(e) => patch("address_ar", e.target.value)} className="input" data-testid="set-address-ar" /></Field>
              <Field label={`${tr("x.showroom_name")} · ${tr("x.lang_ar")}`}><input dir="rtl" value={form.showroom_name_ar || ""} onChange={(e) => patch("showroom_name_ar", e.target.value)} className="input" data-testid="set-name-ar" /></Field>
              <Field label={tr("x.phone")}><input value={form.phone || ""} onChange={(e) => patch("phone", e.target.value)} className="input" /></Field>
              <Field label="WhatsApp"><input value={form.whatsapp || ""} onChange={(e) => patch("whatsapp", e.target.value)} className="input" /></Field>
              <Field label={tr("x.email")}><input value={form.email || ""} onChange={(e) => patch("email", e.target.value)} className="input" /></Field>
            </Section>
          )}

          {active === "branding" && (
            <Section title={t("settings.sub_branding")}>
              <Field label={tr("x.logo_for_dark_backgrounds_sidebar_websit")} wide>
                <HeroImageInput url={form.logo_dark_url || ""} onChange={(u) => patch("logo_dark_url", u)} testid="logo-dark" noPreview />
                {form.logo_dark_url && <div style={{ marginTop: 8, padding: 14, background: "#141416", border: "1px solid var(--divider)" }}><img src={form.logo_dark_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} style={{ height: 44, width: "auto", maxWidth: 220, objectFit: "contain", display: "block" }} /></div>}
              </Field>
              <Field label={tr("x.logo_for_light_backgrounds_invoices_docu")} wide>
                <HeroImageInput url={form.logo_light_url || ""} onChange={(u) => patch("logo_light_url", u)} testid="logo-light" noPreview />
                <div className="text-xs opacity-60 mt-1" data-testid="logo-light-hint">{tr("x.used_on_invoices_and_pdfs_if_empty_pdfs_")}</div>
                {form.logo_light_url && <div style={{ marginTop: 8, padding: 14, background: "#fff", border: "1px solid var(--divider)" }}><img src={form.logo_light_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} style={{ height: 44, width: "auto", maxWidth: 220, objectFit: "contain", display: "block" }} /></div>}
              </Field>
              <Field label={tr("x.primary_colour_buttons_links_active_stat")} wide>
                <ColorPicker label={tr("x.primary")} value={form.primary_color || "#c8102e"}
                  onChange={(v) => patch("primary_color", v)}
                  presets={PRIMARY_PRESETS} testid="primary-picker" />
              </Field>
              <Field label={tr("x.secondary_colour_sidebar_trade_in_band_f")} wide>
                <ColorPicker label={tr("x.secondary")} value={form.secondary_color || "#f4f3f1"}
                  onChange={(v) => patch("secondary_color", v)}
                  presets={SECONDARY_PRESETS} testid="secondary-picker" />
              </Field>
              <Field label={tr("x.live_preview")} wide>
                <ThemePreview primary={form.primary_color || "#c8102e"}
                  secondary={form.secondary_color || "#f4f3f1"} />
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 10 }}>
                  {tr("x.changes_save_when_you_press_save_changes")}
                </div>
              </Field>
              <Field label={tr("x.favicon_url")} wide><HeroImageInput url={form.favicon_url || ""} onChange={(u) => patch("favicon_url", u)} testid="favicon" noPreview /></Field>
            </Section>
          )}

          {active === "currency" && (
            <Section title={t("settings.sub_currency")}>
              <Field label={tr("x.currency")}>
                <select value={form.currency_code} onChange={(e) => setCurrency(e.target.value)} className="input" data-testid="settings-currency">
                  {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                </select>
              </Field>
              <Field label={tr("x.currency_symbol")}><input value={form.currency_symbol || ""} onChange={(e) => patch("currency_symbol", e.target.value)} className="input" /></Field>
              <Field label={tr("x.currency_decimals")}><input type="number" value={form.currency_decimals ?? 3} onChange={(e) => patch("currency_decimals", Number(e.target.value))} className="input" /></Field>
              <Field label={tr("x.locale")}><input value={form.locale || ""} onChange={(e) => patch("locale", e.target.value)} className="input" /></Field>
              <Field label={tr("x.timezone")}><input value={form.timezone || ""} onChange={(e) => patch("timezone", e.target.value)} className="input" /></Field>
              <Field label={tr("x.date_format")}><input value={form.date_format || ""} onChange={(e) => patch("date_format", e.target.value)} className="input" /></Field>
            </Section>
          )}

          {active === "tax" && (
            <Section title={t("settings.sub_tax")}>
              <Field label={tr("x.tax_percentage")}><input type="number" step="0.01" value={form.tax_percentage ?? 0} onChange={(e) => patch("tax_percentage", Number(e.target.value))} className="input" data-testid="settings-tax" /></Field>
              <Field label={tr("x.tax_registration_number")}><input value={form.tax_number || ""} onChange={(e) => patch("tax_number", e.target.value)} className="input" /></Field>
              <Field label={tr("x.invoice_prefix_letters_and_numbers_max_1")}>
                <input value={(form.sale_prefix || "").replace(/-+$/, "")} maxLength={10} onChange={(e) => patch("sale_prefix", e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase())} className="input" data-testid="settings-sale-prefix" />
                <div className="text-xs opacity-60 mt-1" data-testid="settings-sale-prefix-preview">{tr("x.next_invoices_look_like")} {((form.sale_prefix || "INV").replace(/-+$/, "") || "INV")}-{new Date().getFullYear()}{tr("x.0001_old_invoice_numbers_never_change")}</div>
              </Field>
              <Field label={tr("x.payment_terms_days")}><input type="number" value={form.payment_terms_days ?? 30} onChange={(e) => patch("payment_terms_days", Number(e.target.value))} className="input" /></Field>
              <Field label={tr("x.days_in_stock_alert_threshold")}><input type="number" value={form.days_in_stock_threshold ?? 60} onChange={(e) => patch("days_in_stock_threshold", Number(e.target.value))} className="input" /></Field>
            </Section>
          )}

          {active === "website" && (
            <Section title={t("settings.sub_website")}>
              <PublicTextsFields form={form} patch={patch} Field={Field} />
              <Field label={tr("x.website_theme")} wide><ThemePicker value={form.public_theme} onApply={(th) => patch("public_theme", th)} locked={!has("website_themes_all")} /></Field>
              <Field label={tr("x.hero_mode")}>
                <div className="seg" data-testid="hero-mode-seg">
                  <button type="button" className={(form.public_hero_mode || "slider") === "slider" ? "active" : ""} onClick={() => patch("public_hero_mode", "slider")} data-testid="hero-mode-slider">{tr("x.slider")}</button>
                  <button type="button" className={form.public_hero_mode === "single" ? "active" : ""} onClick={() => patch("public_hero_mode", "single")} data-testid="hero-mode-single">{tr("x.single_image")}</button>
                </div>
              </Field>
              {(form.public_hero_mode || "slider") === "single" ? (
                <Field label={tr("x.single_hero_image")} wide>
                  <HeroImageInput
                    url={form.public_hero_image_url || ""}
                    onChange={(u) => patch("public_hero_image_url", u)}
                    testid="hero-single"
                  />
                </Field>
              ) : (
                <Field label={tr("x.hero_slides_1_3_images")} wide>
                  <HeroSlidesInput
                    slides={form.public_hero_slides || []}
                    onChange={(arr) => patch("public_hero_slides", arr)}
                  />
                </Field>
              )}
            </Section>
          )}

          {active === "ai" && (
            <><AiPanel isAdmin={isAdmin} form={form} patch={patch} refresh={load} />{isAdmin && <ImageAiSection />}</>
          )}
          {active === "social" && isAdmin && (
            <Section title={t("settings.sub_social")}><SocialAccountsPanel onSaved={(d) => setForm((f) => ({ ...f, instagram: d.instagram, facebook: d.facebook, whatsapp: d.whatsapp }))} /></Section>
          )}
          {active === "payments" && isAdmin && (
            <Section title={t("settings.sub_payments")}><PaymentGatewayPanel /></Section>
          )}
          {active === "leads" && isAdmin && <Section title={tr("settings.sub_leads")}><LeadsSettingsPanel /></Section>}
          {active === "whatsapp" && isAdmin && <Section title={tr("settings.sub_whatsapp")}><WhatsAppSettingsPanel /><FaqSettingsPanel /></Section>}
          {active === "notifications" && isAdmin && <Section title={tr("settings.sub_notifications")}><NotificationsSettingsPanel /></Section>}
          {active === "branches" && isAdmin && (
            <Section title={tr("br.title")}><BranchesPanel /></Section>
          )}
          {active === "plan" && (
            <Section title={t("settings.sub_plan")}><MyPlanPanel /></Section>
          )}
          {active === "forms" && isAdmin && (
            <Section title={t("settings.sub_forms")}><FormsSettingsPanel /></Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="panel">
      <div className="panel-title"><div className="kicker">{title}</div></div>
      <div className="panel-body grid gap-5 items-end" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), minmax(0, 1fr)))" }}>
        {children}
      </div>
    </div>
  );
}
function Field({ label, children, wide }) {
  return (
    <div style={{ gridColumn: wide ? "1 / -1" : "auto", minWidth: 0 }}>
      <label className="kpi-label block mb-2">{label}</label>
      {children}
    </div>
  );
}

/* Lightweight local wrapper so we can style the AI section inside the sub-page.
   Delegates all logic to the existing AiSettingsSection. */
import AiSettingsSection from "../components/AiSettingsSection";
import ImageAiSection from "../components/ImageAiSection";
import SocialAccountsPanel from "../components/SocialAccountsPanel";
import { isExternalImage } from "../components/BrandLogo";
import { ThemePicker } from "../components/ThemePicker";

function ExternalImageWarning({ url, testid }) {
  if (!isExternalImage(url)) return null;
  let host = url;
  try { host = new URL(url).host; } catch { /* keep raw */ }
  return (
    <div role="alert" className="text-xs" style={{ padding: "8px 12px", background: "#fff7e6", border: "1px solid #f0c36d", color: "#7a4b00" }} data-testid={`${testid}-external-warning`}>
      {tr("x.external_image_warning", { p0: host })}
    </div>
  );
}
import { tr } from "../lib/i18n";
function AiPanel(props) { return <AiSettingsSection {...props} />; }

/* Upload or paste a single hero image URL. */
function HeroImageInput({ url, onChange, testid = "hero", noPreview = false }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const bad = await checkImageFile(file);
    if (bad) { toast.error(bad); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      onChange(data?.url || "");
      toast.success(tr("x.hero_image_uploaded"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input value={url || ""} onChange={(e) => onChange(e.target.value)} placeholder={tr("x.paste_image_url_or_upload")} className="input" dir="ltr" style={{ flex: 1 }} data-testid={`${testid}-url`} />
        <label className="btn-secondary" style={{ cursor: "pointer" }} data-testid={`${testid}-upload`}>
          {busy ? tr("x.uploading") : tr("x.upload")}
          <input type="file" accept="image/*" style={{ display: "none" }} data-testid={`${testid}-file-input`} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        {url && <button type="button" className="btn-ghost" onClick={() => onChange("")} data-testid={`${testid}-clear`}>{tr("x.clear")}</button>}
      </div>
      <ExternalImageWarning url={url} testid={testid} />
      {url && !noPreview && <div style={{ background: "#fff", border: "1px solid var(--divider)", padding: 8 }}><img src={url} alt="" style={{ width: "100%", maxHeight: 160, objectFit: "cover" }} /></div>}
    </div>
  );
}

/* Upload / order up to 3 hero slides. */
function HeroSlidesInput({ slides, onChange }) {
  const list = (slides || []).slice(0, 3);
  const setAt = (i, url) => {
    const next = [...list];
    if (url) next[i] = url; else next.splice(i, 1);
    onChange(next.filter(Boolean));
  };
  const addEmpty = () => { if (list.length < 3) onChange([...list, ""]); };
  const upload = async (i, file) => {
    if (!file) return;
    const bad = await checkImageFile(file);
    if (bad) { toast.error(bad); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setAt(i, data?.url || "");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const rows = Array.from({ length: Math.max(list.length, 1) }, (_, i) => list[i] || "");
  return (
    <div style={{ display: "grid", gap: 10 }} data-testid="hero-slides-input">
      {rows.map((u, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "72px 1fr auto auto", gap: 8, alignItems: "center" }}>
          <div style={{ width: 72, height: 48, background: "var(--surface)", border: "1px solid var(--divider)", overflow: "hidden" }}>
            {u ? <img src={u} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ fontSize: 10, textAlign: "center", lineHeight: "48px", color: "var(--ds-ink-muted)" }}>{tr("x.empty")}</div>}
          </div>
          <input value={u} onChange={(e) => setAt(i, e.target.value)} placeholder={tr("x.slide_url", { p0: i + 1 })} className="input" data-testid={`hero-slide-${i}-url`} />
          <label className="btn-secondary" style={{ cursor: "pointer" }} data-testid={`hero-slide-${i}-upload`}>
            {tr("x.upload")}
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { upload(i, e.target.files?.[0]); e.target.value = ""; }} />
          </label>
          {u && <button type="button" className="btn-ghost" onClick={() => setAt(i, "")} data-testid={`hero-slide-${i}-remove`}>{tr("x.remove")}</button>}
          {isExternalImage(u) && <div style={{ gridColumn: "1 / -1" }}><ExternalImageWarning url={u} testid={`hero-slide-${i}`} /></div>}
        </div>
      ))}
      {list.length < 3 && <button type="button" className="btn-ghost text-xs" style={{ border: "1px solid var(--divider)", width: "fit-content", padding: "6px 12px" }} onClick={addEmpty} data-testid="hero-slide-add">{tr("x.add_slide")}{3 - list.length} {tr("x.left")}</button>}
      <div className="text-xs" style={{ color: "var(--ds-ink-muted)" }}>{tr("x.if_no_slides_are_uploaded_three_bundled_")}</div>
    </div>
  );
}

// Suppress unused-import warnings
void [Sparkles, Send, UserPlus, logoLight];
